import { defineComponent } from 'vue';
import { QIcon, QExpansionItem, QTooltip, QBtn } from 'quasar';

import { resolveGroupAccent } from '../shared/helpers.js';
import { formatDisplayName } from '../shared/displayName.js';
import FlowV2ListToolbar from './flowv2ListToolbar.jsx';
import './toolPalette.css';

function paletteItemMatches(name, filter) {
    const raw = (name || '').toLowerCase();
    if (raw.includes(filter))
        return true;
    return formatDisplayName(name).toLowerCase().includes(filter);
}

function filterPaletteItems(items, searchText) {
    const filter = (searchText || '').trim().toLowerCase();
    if (!filter)
        return items || [];
    return (items || []).map((cat) => ({
        ...cat,
        children: (cat.children || []).filter((c) => paletteItemMatches(c.name, filter)),
    })).filter((cat) => (cat.children || []).length);
}

function tooltipText(tool) {
    const d = tool?.description;
    if (d == null || d === '')
        return 'No description.';
    const s = String(d).trim();
    return s || 'No description.';
}

function categoryAccent(cat) {
    return resolveGroupAccent(cat?.color ?? cat?.accent);
}

const ToolTile = defineComponent({
    name: 'flow-v2-tool-tile',
    components: { QIcon, QTooltip },
    props: {
        tool: { type: Object, required: true },
        accent: { type: Object, required: true },
        categoryName: { type: String, default: '' },
        onDragStart: { type: Function, required: true },
        readOnly: { type: Boolean, default: false },
    },
    methods: {
        handleDragStart(ev) {
            if (this.readOnly) {
                ev.preventDefault();
                return;
            }
            ev.stopPropagation();
            const fn = this.onDragStart;
            if (typeof fn === 'function')
                fn(ev, this.tool);
            const ghost = this.$el;
            if (ghost && typeof ev.dataTransfer?.setDragImage === 'function')
                ev.dataTransfer.setDragImage(ghost, 10, 10);
        },
        handleClick(ev) {
            ev.stopPropagation();
        },
    },
    render() {
        const accent = this.accent || resolveGroupAccent(null);
        return (
            <div
                class="flowv2-tool-tile"
                draggable={!this.readOnly}
                onDragstart={this.handleDragStart}
                onClick={this.handleClick}
            >
                <div
                    class="flowv2-tool-tile-icon"
                    style={{ backgroundColor: accent.well, color: accent.glyph }}
                >
                    <QIcon name={this.tool.icon || 'mdi-wrench'} size="28px" />
                </div>
                <div class="flowv2-tool-tile-label">{formatDisplayName(this.tool.name)}</div>
                {this.categoryName
                    ? <div class="flowv2-tool-tile-cat">{formatDisplayName(this.categoryName)}</div>
                    : null}
                <QTooltip
                    transition-show="scale"
                    transition-hide="scale"
                    anchor="top middle"
                    self="bottom middle"
                    class="text-body2"
                    style={{ maxWidth: '320px' }}
                >
                    {tooltipText(this.tool)}
                </QTooltip>
            </div>
        );
    },
});

export default defineComponent({
    name: 'flow-v2-tool-grid',
    components: {
        QExpansionItem,
        QBtn,
        QTooltip,
        FlowV2ListToolbar,
        ToolTile,
    },
    props: {
        items: { type: Array, default: () => [] },
        onDragStart: { type: Function, required: true },
        onConfigurePalettes: { type: Function, default: null },
        readOnly: { type: Boolean, default: false },
    },
    data() {
        return {
            searchText: '',
            collapsedIds: {},
        };
    },
    computed: {
        isSearching() {
            return !!(this.searchText && String(this.searchText).trim());
        },
        filteredCategories() {
            return filterPaletteItems(this.items, this.searchText);
        },
        flatSearchTiles() {
            if (!this.isSearching)
                return [];
            const tiles = [];
            for (const cat of this.filteredCategories) {
                for (const tool of cat.children || []) {
                    tiles.push({ tool, category: cat });
                }
            }
            return tiles;
        },
    },
    methods: {
        isCategoryExpanded(id) {
            if (this.isSearching)
                return true;
            return !this.collapsedIds[String(id)];
        },
        setCategoryExpanded(id, expanded) {
            if (this.isSearching)
                return;
            const key = String(id);
            const next = { ...this.collapsedIds };
            if (expanded)
                delete next[key];
            else
                next[key] = true;
            this.collapsedIds = next;
        },
        renderTile(tool, cat, showCategory) {
            return (
                <ToolTile
                    key={tool.id}
                    tool={tool}
                    accent={categoryAccent(cat)}
                    categoryName={showCategory ? (cat.name || '') : ''}
                    onDragStart={this.readOnly ? () => {} : this.onDragStart}
                    readOnly={this.readOnly}
                />
            );
        },
    },
    render() {
        const body = this.isSearching
            ? (
                <div class="flowv2-tool-tile-grid">
                    {this.flatSearchTiles.map(({ tool, category }) => this.renderTile(tool, category, true))}
                </div>
            )
            : this.filteredCategories.map((cat) => (
                <QExpansionItem
                    key={cat.id}
                    class="flowv2-tool-palette-cat"
                    dense
                    switchToggleSide
                    expandIcon="mdi-chevron-down"
                    label={formatDisplayName(cat.name)}
                    modelValue={this.isCategoryExpanded(cat.id)}
                    onUpdate:modelValue={(v) => this.setCategoryExpanded(cat.id, v)}
                >
                    <div class="flowv2-tool-tile-grid">
                        {(cat.children || []).map((tool) => this.renderTile(tool, cat, false))}
                    </div>
                </QExpansionItem>
            ));

        return (
            <div class="flowv2-tool-palette">
                <FlowV2ListToolbar
                    modelValue={this.searchText}
                    onUpdate:modelValue={(v) => { this.searchText = v; }}
                >
                    <QBtn
                        flat
                        round
                        dense
                        icon="mdi-cog"
                        color="blue-grey-8"
                        onClick={(ev) => {
                            ev.stopPropagation();
                            if (typeof this.onConfigurePalettes === 'function')
                                this.onConfigurePalettes();
                        }}
                    >
                        <QTooltip>Palette settings</QTooltip>
                    </QBtn>
                </FlowV2ListToolbar>
                <div class="flowv2-tool-palette-scroll">
                    {body}
                </div>
            </div>
        );
    },
});
